import {
  Badge,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';

import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { observer } from 'mobx-react-lite';
import config from '../../config';
import useDocuments, { isReactNativeWebView } from '../../hooks/useDocuments';
import useMedia from '../../hooks/useMedia';
import { useStore } from '../../store/context';
import { NavMenuItem, NavMenueItems } from './MenueItems';

const NavMenuList = observer((): JSX.Element => {
  const [selected, setSelected] = useState<NavMenuItem | null>(null);
  const { documentsFromTiles } = useDocuments(false, true);

  const docs = documentsFromTiles
    .map(documentGroup => documentGroup?.documents || [])
    .flat();
  const [scrollY, setScrollY] = useState(0);
  const isMobile = useMedia('md');
  const clientKey = process.env.REACT_APP_CLIENT || 'wbg';
  const configuration = config[clientKey] ?? config.wbg;
  const isEbgConfiguration = clientKey.startsWith('ebg');

  const navigate = useNavigate();
  const { authStore, welcomeStore, userStore, messageStore } = useStore();

  const welcomeData = welcomeStore.getWelcomeData();
  const user = welcomeData?.userData;

  const handleListItemClick = (item: NavMenuItem) => {
    setSelected(item);

    if (item.name === 'Logout') {
      authStore.removeUserData();
      welcomeStore.setWelcomeData(undefined);
      welcomeStore.currentEstateId = undefined;
    }

    if (item.name === 'Reservierungen' && isEbgConfiguration) {
      window.open(configuration.bookingLink, '_blank');
      return;
    }
    navigate(item.path);
  };
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    const currentItem = NavMenueItems.find(item => item.path === path);
    if (currentItem) {
      setSelected(currentItem);
    }
  }, [location]);

  const getBadge = (item: NavMenuItem) => {
    if (item.name === 'Ungelesene Dokumente') return docs.length ?? 0;
    if (item.name === 'Mitteilungen')
      return (
        messageStore.messages?.filter(message => !message.isRead)?.length ?? 0
      );
    return 0;
  };

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY < 180) {
        setScrollY(window.scrollY);
      } else {
        setScrollY(180);
      }
    };
    handleScroll();

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const getMenuItems = () => {
    let navItems = NavMenueItems;

    if (
      !isEbgConfiguration &&
      (!configuration?.hasReservations || !user?.reservationAccess)
    ) {
      navItems = navItems.filter(elem => elem.name !== 'Reservierungen');
    }
    if (!process.env.REACT_APP_CLIENT?.includes('wbg')) {
      navItems = navItems.filter(
        elem =>
          elem.name !== 'Sprechtage' && elem.name !== 'Haushaltsversicherung',
      );
    }
    if (process.env.REACT_APP_CLIENT?.includes('ebg')) {
      navItems = navItems.filter(elem => elem.name !== 'Kontoauszug');
    }
    if (userStore.getBlackBoard() === false) {
      navItems = navItems.filter(elem => elem.name !== 'Schwarzes Brett');
    }
    if (!welcomeData?.oilPrices || welcomeData?.oilPrices?.length === 0) {
      navItems = navItems.filter(elem => elem.name !== 'Ölpreise');
    }

    if (
      'ReactNativeWebView' in window &&
      isReactNativeWebView(window.ReactNativeWebView)
    ) {
      navItems = navItems.filter(elem => elem.name !== 'Logout');
    }

    return navItems.map(item => (
      <ListItem key={item.name} disablePadding>
        <ListItemButton onClick={() => handleListItemClick(item)}>
          <ListItemIcon>{item.icon || null}</ListItemIcon>
          <Badge badgeContent={getBadge(item)} color="primary">
            <ListItemText
              primary={
                <Typography
                  variant="subtitle1"
                  color={item.id === selected?.id ? 'primary' : 'secondary'}
                  sx={{
                    textDecoration:
                      item.id === selected?.id ? 'underline' : 'none',
                  }}
                >
                  {item.name.toUpperCase()}
                </Typography>
              }
            />
          </Badge>
        </ListItemButton>
      </ListItem>
    ));
  };

  return (
    <Box
      sx={{
        position: isMobile ? 'static' : 'fixed',
        top: isMobile ? 0 : 180 - scrollY,
      }}
    >
      <List component="nav">{getMenuItems()}</List>
    </Box>
  );
});

export default NavMenuList;
